import { useMemo } from "react";
import { Box, FormHelperText } from "@mui/material";
import { CertificateUpload } from "@components/FileUpload/FileUpload";
import type { FileUploadConditions } from "@components/FileUpload/FileUpload.types";
import SingleFile from "@components/FileUpload/SingleFile";
import { useUploadStore, type UploadEntry } from "@components/FileUpload/uploadStore";
import FormItem, { getErrorMessage } from "@components/FormItem/FormItem";
import type { VRCertificateType } from "@constants/certificates";
import { useNotification } from "@context/NotificationContext";
import type { VesselRegisterFormData } from "@features/vessel-register/schema/vesselRegisterFormSchema";
import { generateCertificateField } from "@features/vessel-register/utils";
import { t } from "i18next";
import type { FieldPath, FieldPathValue } from "react-hook-form";
import { useOutletContext } from "react-router-dom";
import type { VesselOutletContext } from "../VesselRegistrationContainer";
import { useUploadCertificate } from "./useUploadCertificate";

type SingleCertificateProps = {
  uploadConditions: FileUploadConditions;
  additionalClassNames?: string;
  files: UploadEntry[];
  setFiles: (files: UploadEntry[]) => void;
  removeFn: (fieldName: string) => void;
  updateFormData: <TPath extends FieldPath<VesselRegisterFormData>>(
    key: TPath,
    value: FieldPathValue<VesselRegisterFormData, TPath>,
  ) => void;
  testId?: string;
  fieldName: VRCertificateType;
  canBePermanent?: boolean;
};

const SingleCertificate = ({
  uploadConditions,
  files,
  setFiles,
  removeFn,
  updateFormData,
  fieldName,
  canBePermanent = false,
}: SingleCertificateProps) => {
  const {
    formData: {
      formState: { errors },
      register,
      trigger,
      setValue,
    },
    setError,
    clearErrors,
    watch,
  } = useOutletContext<VesselOutletContext>();
  const { notify } = useNotification();

  const { setFilesForKey, clearFilesForKey } = useUploadStore();

  const urlPath = generateCertificateField.url(fieldName);
  const expiryPath = generateCertificateField.expiryDate(fieldName);
  const classPath = generateCertificateField.className(fieldName);
  const docNamePath = generateCertificateField.documentName(fieldName);
  const permanentPath = generateCertificateField.permanent(fieldName);

  const permanent = watch(permanentPath) === true && canBePermanent;

  const certLevelError = getErrorMessage(errors, urlPath);

  const allowedExtensions = useMemo(
    () =>
      (uploadConditions.format ?? []).map((ext) =>
        ext.startsWith(".") ? ext.toLowerCase() : `.${ext.toLowerCase()}`,
      ),
    [uploadConditions.format],
  );

  const onSuccess = ({
    certificateType,
    documentUrl,
    documentName,
  }: {
    certificateType: VRCertificateType;
    documentUrl: string;
    documentName: string;
  }) => {
    const urlKey = generateCertificateField.url(certificateType);
    const documentNameKey = generateCertificateField.documentName(certificateType);

    setValue(urlKey, documentUrl, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: false,
    });
    setValue(documentNameKey, documentName, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: false,
    });
    updateFormData(urlKey, documentUrl);
    updateFormData(documentNameKey, documentName);
    clearErrors(urlKey);

    const entry: UploadEntry = {
      name:
        typeof documentName === "string" && documentName.trim().length > 0
          ? documentName
          : (documentUrl.split("/").pop() ?? certificateType),
      url: documentUrl,
      certificateType,
    };

    setFilesForKey(certificateType, [entry]);
    trigger(generateCertificateField.className(certificateType));
  };

  const onError = (certificateType: VRCertificateType) => {
    notify({
      type: "error",
      title: t("vessel-management.notifications.certificate-upload-error-title"),
      description: t("vessel-management.notifications.certificate-upload-error-description"),
    });

    clearFilesForKey(certificateType);
  };

  const mutation = useUploadCertificate(onSuccess, onError);

  const handleFieldChange = async (certType: VRCertificateType, value: unknown) => {
    const files = value as File[] | undefined;
    const file = files?.[0];

    if (file) {
      mutation.mutate({ file, certificateType: certType });
    }
  };

  const handleReject = () => {
    const urlKey = generateCertificateField.url(fieldName);

    const typesLabel = uploadConditions.format
      ?.map((type) => type.toUpperCase().replace(".", ""))
      .join(", ");
    setError(urlKey, {
      type: "manual",
      message: t("form-validation.certificate-size-and-type", {
        max: uploadConditions.maxSizeLabel,
        types: typesLabel,
      }),
    });
  };

  return (
    <div className="flex py-4 px-8 gap-4 flex-col">
      <div>
        <input type="hidden" {...register(urlPath)} />
        <input type="hidden" {...register(docNamePath)} />

        <CertificateUpload
          testId={`${fieldName}-upload`}
          onUpload={(files) => handleFieldChange(fieldName, files)}
          onReject={handleReject}
          maxFiles={1}
          name={fieldName}
          conditions={uploadConditions}
          allowedExtensions={allowedExtensions}
          files={files}
          setFiles={setFiles}
          isError={!!certLevelError}
          disabled={!!files[0]}
        />

        {certLevelError && (
          <FormHelperText
            sx={{
              ml: 0,
              fontWeight: 500,
              fontSize: 12,
              marginY: "4px",
              height: "20px",
              color: "error.main",
            }}
          >
            {certLevelError}
          </FormHelperText>
        )}
        {files[0] && (
          <Box sx={{ marginTop: "28px" }}>
            <SingleFile
              testId={fieldName}
              file={files[0]}
              fieldName={fieldName}
              setFiles={() => setFiles([] as UploadEntry[])}
              removeFn={() => removeFn(fieldName)}
              isLoading={mutation.isPending}
            />
          </Box>
        )}
      </div>

      <div
        className={`grid grid-cols-1 gap-4 ${canBePermanent ? "xl:grid-cols-[1fr_1fr_0.5fr]" : "xl:grid-cols-2"}`}
      >
        <FormItem<VesselRegisterFormData, typeof classPath>
          testId={`${fieldName}-class-name`}
          name={classPath}
          type="text"
          label={t("vessel-management.registry-class-name")}
          onFieldChange={updateFormData}
        />
        <FormItem<VesselRegisterFormData, typeof expiryPath>
          testId={`${fieldName}-expiration`}
          name={expiryPath}
          type="date-picker"
          label={t("vessel-management.expiration-date")}
          onFieldChange={(k, v) => {
            if (!v) trigger(expiryPath);
            updateFormData(k, v);
          }}
          timePicker={false}
          required
          dateConstraint={permanent ? undefined : "not-past"}
          disabled={permanent}
        />
        {canBePermanent && (
          <div className="flex xl:items-end">
            <FormItem<VesselRegisterFormData, typeof permanentPath>
              testId={`${fieldName}-permanent`}
              defaultValue={permanent}
              name={permanentPath}
              type="checkbox"
              label={t("common.permanent")}
              onFieldChange={(k, v) => {
                if (v) {
                  clearErrors(expiryPath);
                  setValue(expiryPath, null, {
                    shouldDirty: true,
                    shouldTouch: true,
                    shouldValidate: false,
                  });
                  updateFormData(expiryPath, null);
                } else {
                  trigger(expiryPath);
                }
                updateFormData(k, v);
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default SingleCertificate;
